import { DynamicContentListData, TranslationData } from '../model.ts';
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { FilterConfig } from '../../../../../components/blackrock/advisor-center/filter-dialog/model.ts';
import {
	getRelevantAuthors,
	getRelevantPublications,
	getRelevantTopics,
} from '../helper/seriesItem.ts';
import { FILTERABLE_TYPES } from '../../../../../components/blackrock/advisor-center/_types/item.ts';
import { SeriesItemData } from '../../../../../components/blackrock/advisor-center/cards/series-item-card/SeriesItemCardProps.ts';

/**
 * Custom hook to filter SeriesItemData list.
 * @param {SeriesItemData[]} list - The series item list to filter.
 * @param {DynamicContentListData} data - The DynamicContentList data to init filter config (filterOptions).
 * @param {TranslationData} translation - The translation data to init filter config (filterOptions).
 * @param {FilterConfig | undefined} initialFilter - The initial filter configuration.
 * @param {boolean} filterDisabled - Is the filter functionality disabled?.
 * @returns {SeriesItemData[]} filteredList - The filtered list.
 * @returns {FilterConfig} filterOptions - The filter options for the Filter component.
 * @returns {FilterConfig} selectedFilter - The selected filters (for the Filter component).
 * @returns {Dispatch<SetStateAction<FilterConfig>>} setSelectedFilter - Function to set selected filters (for the Filter component).
 * @returns {(filter: FilterConfig) => SeriesItemData[]} getFilteredList - Function to get filtered list. Calling this function doesn't affect on the filtered list. It is used for precalculate the number of the result of filtering.
 */
export const useFilter = (
	list: SeriesItemData[],
	data: DynamicContentListData,
	translation: TranslationData,
	initialFilter: FilterConfig | undefined,
	filterDisabled?: boolean
): [
	SeriesItemData[],
	FilterConfig,
	FilterConfig,
	Dispatch<SetStateAction<FilterConfig>>,
	(filter: FilterConfig) => SeriesItemData[],
] => {
	if (filterDisabled) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return [list, null, null, null, null];
	} else {
		const filterOptions = useMemo(
			() => getFilterOptions(list, data, translation),
			[list, data, translation]
		);

		const [selectedFilter, setSelectedFilter] = useState<FilterConfig>(
			initSelectedFilter(filterOptions, initialFilter)
		);

		const doesItemMeetFilterCriteria = useCallback(
			(item: SeriesItemData, newFilter: FilterConfig) => {
				const itemAuthorList = item.authors.map(({ authorName }) => authorName);

				return (
					//Filter other authors if selected
					(newFilter.authors.includes(translation.filter.otherAuthors) &&
						!filterOptions.authors.some((author) =>
							itemAuthorList.includes(author)
						)) ||
					//Filter publications
					newFilter.publications.some((publication) =>
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						item.publications.includes(publication)
					) ||
					//Filter authors
					newFilter.authors.some((author) => itemAuthorList.includes(author)) ||
					//Filter topics
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					newFilter.topics.some((topic) => item.topics.includes(topic)) ||
					//Filter item type
					newFilter.types.includes(translation.types[item.itemType])
				);
			},
			[list, filterOptions, data.topicMap, translation]
		);

		const getFilteredList = useCallback(
			(newFilter: FilterConfig): SeriesItemData[] => {
				if (Object.values(newFilter).every((filters) => filters.length == 0)) {
					return list;
				} else {
					return list.filter((item) =>
						doesItemMeetFilterCriteria(item, newFilter)
					);
				}
			},
			[list, filterOptions, data.topicMap, translation]
		);

		const filteredList = useMemo(() => {
			return getFilteredList(selectedFilter);
		}, [list, filterOptions, selectedFilter, data.topicMap, translation]);

		return [
			filteredList,
			filterOptions,
			selectedFilter,
			setSelectedFilter,
			getFilteredList,
		];
	}
};

// initialize and validate the selected filter configuration. (if filer options doesn't contain the initial value, it will be removed)
const initSelectedFilter = (
	filterOptions: FilterConfig,
	initialFilter?: FilterConfig
): FilterConfig => ({
	types: intersect(initialFilter?.types, filterOptions.types),
	authors: intersect(initialFilter?.authors, filterOptions.authors),
	topics: intersect(initialFilter?.topics, filterOptions.topics),
	publications: intersect(
		initialFilter?.publications,
		filterOptions.publications
	),
});

// create filter configuration
const getFilterOptions = (
	list: SeriesItemData[],
	data: DynamicContentListData,
	translation: TranslationData
): FilterConfig => {
	// We collect all the topics which exists in the series item list
	// Filter component will receive the relevantTopics only.
	const relevantTopics = getRelevantTopics(list);

	const relevantPublications = getRelevantPublications(list);

	// List of author (data.authorList) is provided by DCR configuration.
	// We must show those what will bring at least one result.
	// Filter component will receive the relevantAuthors only.
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const relevantAuthors = getRelevantAuthors(data.authorList, list);

	// Additional rule for authors:
	// Not all the items can be filtered by visible authors.
	// That's why we will provide a fixed option as last one.
	// The purpose would be that to make the "rest" visible.
	// It will be visible if it will bring at least one result.
	const isOtherAuthorsOn = list.some(({ authors }) => {
		const authorList = authors.map(({ authorName }) => authorName);
		return !relevantAuthors.some((author) => authorList.includes(author));
	});

	return {
		types: [
			// List of visible types
			...FILTERABLE_TYPES.filter((key) => translation.types[key]).map(
				(key) => translation.types[key]
			),
		],
		topics: [
			// List of visible topic labels
			...relevantTopics,
		],
		publications: [
			// List of visible topic labels
			...relevantPublications,
		],
		authors: [
			// List of visible authors
			...relevantAuthors,
			// Fix filter option for "rest" items
			...(isOtherAuthorsOn ? [translation.filter.otherAuthors] : []),
		],
	};
};

const intersect = <T,>(
	array1: T[] | undefined | null,
	array2: T[] | undefined | null
): T[] => {
	if (!array1 || !array2) {
		return [];
	}
	return array1.filter((i) => array2.includes(i));
};
